/**
 * Storage tier catalog (PE-954). Storage isn't provisioned by default: every
 * tier is requested via a ticket. Standard is free; Performance and Extreme are
 * commercial tiers our team follows up on after a request. NFS and S3 are
 * provisioned for every tenant (S3 also serves HTTP via presigned URLs); the
 * tiers differ by capacity. Shared so the client renders and the server composes
 * the request ticket from one source of truth.
 */

export interface StorageTier {
  id: StorageTierId;
  /** Customer-facing name. */
  name: string;
  /** Typical workloads this tier suits. */
  bestFor: string;
  /** Capacity provided with this tier, in TiB. */
  capacityTiB: number;
  /** Free of charge (still requested via a ticket like every tier). */
  free?: boolean;
  /** Commercial tier: our team follows up on terms after a request. */
  commercial?: boolean;
}

export const STORAGE_TIERS: readonly StorageTier[] = [
  {
    id: "standard",
    name: "Standard",
    bestFor: "Datasets at rest, home and project directories, and dev/test",
    capacityTiB: 10,
    free: true,
  },
  {
    id: "performance",
    name: "Performance",
    bestFor: "Active training datasets and larger shared workloads",
    capacityTiB: 100,
    commercial: true,
  },
  {
    id: "extreme",
    name: "Extreme",
    bestFor: "The largest datasets and the most demanding workloads",
    capacityTiB: 500,
    commercial: true,
  },
];

export type StorageTierId = "standard" | "performance" | "extreme";

export const STORAGE_TIER_IDS = STORAGE_TIERS.map((t) => t.id) as [
  StorageTierId,
  ...StorageTierId[],
];

/** The tier selected by default in the request form (the free baseline). */
export const DEFAULT_STORAGE_TIER: StorageTierId = "standard";

export function storageTier(id: StorageTierId): StorageTier {
  const tier = STORAGE_TIERS.find((t) => t.id === id);
  if (!tier) {
    throw new Error(`Unknown storage tier: ${id}`);
  }
  return tier;
}

/** Human-readable capacity for a tier, e.g. "100 TiB". */
export function tierCapacityLabel(tier: StorageTier): string {
  return `${tier.capacityTiB} TiB`;
}
